/**
 * Game State Store - Client-side chess state management
 *
 * Manages local chess state using @cloudnest/augmented-chess-engine.
 * The server acts as a dumb relay -- all game logic runs here on the client.
 *
 * Event sourcing model:
 * - Host creates initial state and submits as "init" event
 * - Actions are validated + applied locally, then submitted as "action" events
 * - Remote events received via WS are applied through the local engine
 * - On reconnect, getEvents() replays all events from initial state
 * - Game-over is detected locally, then submitted as "game_over" event
 */

import type {
  ChessAction,
  ChessState,
  Color,
} from "@cloudnest/augmented-chess-engine";
import {
  AUGMENTATION_CHOICES_PER_ROUND,
  AUGMENTATION_TRIGGER_TURNS,
  applyMove,
  applySelectAugmentation,
  createInitialState,
  getGameResult,
  getRandomAugmentationChoices,
  normalizeChessState,
  tryApplyAction,
} from "@cloudnest/augmented-chess-engine";
import { useSyncExternalStore } from "react";
import { isPlatformGamePath } from "../../shared/routing/game-path-mode";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
  type GameEvent,
} from "../../shared/services/game-api";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function resolveChessGameApiMode(): GameApiMode {
  if (typeof window === "undefined") return "legacy";
  return isPlatformGamePath("chess", window.location.pathname)
    ? "game-platform"
    : "legacy";
}

function getGameApi(): GameApi {
  const nextMode = resolveChessGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("chess", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

/** Game state store state */
interface GameStateStoreState {
  gameState: ChessState | null;
  roomId: string | null;
  playerColor: Color;
  isHost: boolean;
  lastAppliedSeq: number;
  lastError: string | null;
}

/** Initial state */
const initialState: GameStateStoreState = {
  gameState: null,
  roomId: null,
  playerColor: "WHITE",
  isHost: false,
  lastAppliedSeq: 0,
  lastError: null,
};

/** Current state */
let state: GameStateStoreState = { ...initialState };

/** Listeners */
const listeners = new Set<() => void>();

/** Notify listeners */
function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Raw (unvalidated) action dispatch for trusted replay paths.
 * Returns the new state or null if the action type is unknown.
 */
function rawApplyChessAction(
  gameState: ChessState,
  action: ChessAction,
): ChessState | null {
  if (action.type === "MOVE") {
    return applyMove(gameState, action);
  }
  if (action.type === "SELECT_AUGMENTATION") {
    return applySelectAugmentation(gameState, action);
  }
  if (action.type === "RESIGN") {
    return gameState;
  }
  return null;
}

/**
 * If the action is a MOVE that will trigger a mid-game augmentation round,
 * attach pre-rolled choices so every client applies the same set.
 */
function enrichMoveWithAugmentationChoices(
  gameState: ChessState,
  action: ChessAction,
): ChessAction {
  if (action.type !== "MOVE") return action;

  // action is narrowed to MoveAction by discriminated union
  const movingColor = gameState.pieces.find(
    (p) => p.id === action.pieceId,
  )?.color;
  if (movingColor !== "BLACK") return action;

  const nextTurn = gameState.turnNumber + 1;
  const triggerTurn = AUGMENTATION_TRIGGER_TURNS[gameState.augmentationRound];
  if (triggerTurn === undefined || nextTurn !== triggerTurn) return action;

  const alreadyActive = [
    ...gameState.augmentations.white.active,
    ...gameState.augmentations.black.active,
  ];

  return {
    ...action,
    triggeredAugmentationChoices: {
      whiteChoices: getRandomAugmentationChoices(
        AUGMENTATION_CHOICES_PER_ROUND,
        alreadyActive,
      ),
      blackChoices: getRandomAugmentationChoices(
        AUGMENTATION_CHOICES_PER_ROUND,
        alreadyActive,
      ),
    },
  };
}

/**
 * Fold a persisted event log into the final chess state. The single source of
 * truth for replay, shared by `replayEvents` (guest/reconnect sync) and the
 * host-reconnect guard in `initializeAsHost`, so both reconstruct the same
 * state the live event path produces.
 */
function replayChessEvents(events: readonly GameEvent[]): {
  gameState: ChessState | null;
  lastSeq: number;
} {
  let currentState: ChessState | null = null;
  let lastSeq = 0;

  for (const event of events) {
    if (event.eventType === "init") {
      currentState = normalizeChessState(
        event.payload.state as Record<string, unknown>,
      );
      lastSeq = event.sequenceNumber;
    } else if (event.eventType === "action" && currentState) {
      const action = event.payload as unknown as ChessAction;
      const newState = rawApplyChessAction(currentState, action);
      if (newState) {
        currentState = newState;
      }
      lastSeq = event.sequenceNumber;
    } else if (event.eventType === "game_over") {
      lastSeq = event.sequenceNumber;
    }
  }

  return { gameState: currentState, lastSeq };
}

/** Game state actions */
export const gameStateActions = {
  /**
   * Initialize game as host. Fetches the persisted event log FIRST: if any
   * events exist the host is reconnecting (page reload / remount), so REPLAY
   * them instead of submitting a new "init" — which would overwrite the
   * in-progress game and reset it to the start. Only the genuinely-new-game
   * path (empty log) creates the initial state and submits "init".
   */
  initializeAsHost: async (
    roomId: string,
    playerColor: Color,
  ): Promise<void> => {
    state = {
      ...state,
      gameState: null,
      roomId,
      playerColor,
      isHost: true,
      lastError: null,
    };
    notify();

    const eventsResponse = await getGameApi().getGameEvents(roomId);
    const existingEvents = eventsResponse.success
      ? (eventsResponse.data ?? [])
      : [];

    if (existingEvents.length > 0) {
      const { gameState, lastSeq } = replayChessEvents(existingEvents);
      state = {
        ...state,
        gameState,
        lastAppliedSeq: lastSeq,
        lastError: null,
      };
      notify();
      return;
    }

    // No events yet — brand-new game: create initial state and persist it.
    const initial = createInitialState();
    state = {
      ...state,
      gameState: initial,
      lastAppliedSeq: 0,
      lastError: null,
    };
    notify();

    const response = await getGameApi().submitEvent(roomId, "init", {
      state: initial,
    });

    if (response.success && response.data) {
      state = { ...state, lastAppliedSeq: response.data.sequenceNumber };
      notify();
    }
  },

  /**
   * Initialize game as guest: wait for "init" event via WS or event replay.
   */
  initializeAsGuest: (roomId: string, playerColor: Color): void => {
    state = {
      ...state,
      gameState: null,
      roomId,
      playerColor,
      isHost: false,
      lastAppliedSeq: 0,
      lastError: null,
    };
    notify();
  },

  /**
   * Replay all events from the server (for reconnect / initial sync).
   */
  replayEvents: async (roomId: string): Promise<void> => {
    const response = await getGameApi().getGameEvents(roomId);

    if (!response.success || !response.data) {
      state = {
        ...state,
        lastError:
          "\uC774\uBCA4\uD2B8\uB97C \uBD88\uB7EC\uC62C \uC218 \uC5C6\uC2B5\uB2C8\uB2E4",
      };
      notify();
      return;
    }

    const { gameState, lastSeq } = replayChessEvents(response.data);
    state = {
      ...state,
      gameState,
      lastAppliedSeq: lastSeq,
      lastError: null,
    };
    notify();
  },

  /**
   * Apply a remote event received via WS.
   */
  applyRemoteEvent: (
    eventType: string,
    payload: Record<string, unknown>,
    seq: number,
  ): void => {
    // Skip events we've already applied
    if (seq <= state.lastAppliedSeq) return;

    if (eventType === "init") {
      const initState = normalizeChessState(
        payload.state as Record<string, unknown>,
      );
      state = {
        ...state,
        gameState: initState,
        lastAppliedSeq: seq,
      };
      notify();
      return;
    }

    if (eventType === "action" && state.gameState) {
      const action = payload as unknown as ChessAction;
      const result = tryApplyAction(state.gameState, action);
      if (result.success) {
        state = {
          ...state,
          gameState: result.state,
          lastAppliedSeq: seq,
        };
        notify();
      } else {
        // Validation failed — likely a WS echo of an already-applied action.
        // Silently skip, just update sequence number.
        state = { ...state, lastAppliedSeq: seq };
        notify();
      }
      return;
    }

    if (eventType === "game_over") {
      state = { ...state, lastAppliedSeq: seq };
      notify();
    }
  },

  /**
   * Apply a local action optimistically and submit to server.
   * On failure, rolls back to previous state.
   */
  submitAction: async (action: ChessAction): Promise<boolean> => {
    const { gameState, roomId } = state;
    if (!gameState || !roomId) return false;

    // Pre-roll augmentation choices for moves that trigger a mid-game round
    // so both clients use the same deterministic choices
    const enrichedAction = enrichMoveWithAugmentationChoices(gameState, action);

    // Validate then apply
    const result = tryApplyAction(gameState, enrichedAction);
    if (!result.success) {
      state = {
        ...state,
        lastError: result.error,
      };
      notify();
      return false;
    }

    const previousState = gameState;
    const newState = result.state;
    state = {
      ...state,
      gameState: newState,
      lastError: null,
    };
    notify();

    // Submit to server
    const response = await getGameApi().submitEvent(
      roomId,
      "action",
      enrichedAction as unknown as Record<string, unknown>,
    );

    if (!response.success) {
      // Rollback on failure
      state = {
        ...state,
        gameState: previousState,
        lastError: response.error ?? "\uC774\uB3D9 \uC2E4\uD328",
      };
      notify();
      return false;
    }

    if (response.data) {
      state = {
        ...state,
        lastAppliedSeq: response.data.sequenceNumber,
      };
      notify();
    }

    // Check for game-over after applying the action
    const gameResult = getGameResult(newState);
    if (gameResult.ended && action.type !== "RESIGN") {
      const isDraw = gameResult.result === "DRAW";
      await getGameApi().submitEvent(roomId, "game_over", {
        winnerId: isDraw ? null : gameResult.result,
        isDraw,
        reason: gameResult.reason,
      });
    }

    return true;
  },

  /**
   * Submit a resignation action.
   */
  submitResignation: async (color: Color): Promise<void> => {
    const { roomId } = state;
    if (!roomId) return;

    const action: ChessAction = {
      type: "RESIGN",
      color,
      timestamp: Date.now(),
    };

    await getGameApi().submitEvent(
      roomId,
      "action",
      action as unknown as Record<string, unknown>,
    );

    // Submit game_over event
    const winner = color === "WHITE" ? "BLACK" : "WHITE";
    await getGameApi().submitEvent(roomId, "game_over", {
      winnerId: winner,
      isDraw: false,
      reason: "RESIGNATION",
    });
  },

  /** Update game state directly (for optimistic updates from own actions) */
  setGameState: (gameState: ChessState): void => {
    state = { ...state, gameState };
    notify();
  },

  /** Get current game state (non-reactive, for imperative checks) */
  getGameState: (): ChessState | null => {
    return state.gameState;
  },

  /** Set player info for reload recovery (roomId, color, isHost) */
  setPlayerInfo: (roomId: string, color: Color, isHost: boolean): void => {
    state = { ...state, roomId, playerColor: color, isHost };
    notify();
  },

  /** Clear error */
  clearError: (): void => {
    state = { ...state, lastError: null };
    notify();
  },

  /** Reset to initial state */
  reset: (): void => {
    state = { ...initialState };
    notify();
  },
};

/** Game state store */
export const gameStateStore = {
  getState: (): GameStateStoreState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

/** Hook to access game state */
export function useGameState(): GameStateStoreState {
  return useSyncExternalStore(
    gameStateStore.subscribe,
    gameStateStore.getState,
  );
}

/** Hook to access game state actions */
export function useGameStateActions(): typeof gameStateActions {
  return gameStateActions;
}
